import { PostAttachmentType } from '__generated__/graphql/legacy/graphql';
import { useTranslation } from 'react-i18next';
import ReactPlayer from 'react-player';
import { isLoomUrl } from 'utils/urlParser';

import React, { useState } from 'react';

import { Modal } from '@thinkific/chalk/Modal';
import { TextField } from '@thinkific/chalk/TextField';

import { Attachment } from 'schema/Post/types';

import * as css from './VideoEmbedModal.css';

interface VideoEmbedModalProps {
  open: boolean;
  attachmentUrl?: string | null;
  onSuccess: (attachment?: Attachment) => void;
  onClose: () => void;
}

export const VideoEmbedModal = ({
  open,
  attachmentUrl,
  onSuccess,
  onClose,
}: VideoEmbedModalProps) => {
  const [videoUrl, setVideoUrl] = useState(attachmentUrl || '');
  const [errorMessage, setErrorMessage] = useState('');
  const { t } = useTranslation();

  const handleValidation = () => {
    let urlIsValid = true;
    let newErrorMessage = '';

    if (!videoUrl.trim()) {
      urlIsValid = false;
      newErrorMessage = t(
        'components-videoEmbedModal-pleaseEnterVideoUrl',
        'Please enter a video URL'
      );
      setErrorMessage(newErrorMessage);
      return urlIsValid;
    }

    const isPlayableUrl = ReactPlayer.canPlay?.(videoUrl) || isLoomUrl(videoUrl);

    if (!isPlayableUrl) {
      urlIsValid = false;
      newErrorMessage = t('components-videoEmbedModal-invalidUrl', 'Invalid URL');
    }
    setErrorMessage(newErrorMessage);
    return urlIsValid;
  };

  const handlePrimaryAction = () => {
    if (handleValidation()) {
      onSuccess({
        fileName: '',
        fileSizeInBytes: null,
        filestackId: '',
        s3Key: '',
        s3Bucket: '',
        url: videoUrl,
        attachmentUrl: videoUrl,
        attachmentType: PostAttachmentType.Video, // We call link attachments "video" due to backwards compatibility, video uploads are "video_upload"
        isLoading: true,
      });

      setVideoUrl('');
      setErrorMessage('');
    }
  };

  const handleModalClose = () => {
    setVideoUrl('');
    setErrorMessage('');
    onClose();
  };

  const handleChange = (value: string) => {
    setVideoUrl(value);

    if (errorMessage) {
      setErrorMessage('');
    }
  };

  return (
    <Modal
      open={open}
      primaryAction={{
        label: t('Embed', 'Embed'),
        onClick: handlePrimaryAction,
        dataQa: 'attachment-video-link-submit',
      }}
      size="md"
      title={t('Embed Video', 'Embed Video')}
      onClose={handleModalClose}
    >
      <TextField
        className={css.urlField}
        dataQa="attachment-video-input__input"
        error={errorMessage}
        label={t('components-attachmentLinkInput-paste_video_url', 'Video url')}
        placeholder={t(
          'components-attachmentLinkInput-supported_video_platforms_tip',
          'YouTube, Vimeo, Loom'
        )}
        type="url"
        value={videoUrl}
        autoFocus
        required
        onChange={handleChange}
      />
    </Modal>
  );
};

export default VideoEmbedModal;
